import { makeUrl } from '@ms-cloudpack/path-string-parsing';
import type { TaskReporter } from '@ms-cloudpack/task-reporter';
import type { TRPCClient } from '@trpc/client';
import { createTRPCClient, createWSClient, httpBatchLink, splitLink, wsLink } from '@trpc/client';
import { ensureWebsocketDefined } from '../utilities/ensureWebsocketDefined.js';
import { ignoreSelfSignedCertErrors } from '../utilities/ignoreSelfSignedCertErrors.js';
import type { AppRouter } from '../types/AppRouter.js';

export type CloudpackClient = TRPCClient<AppRouter> & {
  close: () => Promise<void>;
};

/**
 * Creates a cloudpack client that can be used to communicate with the cloudpack server using trpc.
 */
export async function createCloudpackClient(options: {
  /** API server URL (`ws:` or `wss:`) */
  url: string;
  /** Task reporter for use on the server */
  reporter?: TaskReporter;
  /** Triggered when a WebSocket connection is established */
  onOpen?: () => void;
}): Promise<CloudpackClient> {
  const { url: wsUrl, reporter, onOpen } = options;

  // We polyfill websocket in node conditions.
  await ensureWebsocketDefined();

  ignoreSelfSignedCertErrors({ reporter });

  // Ignore these wsClient `onError` events as we handle them with try/catch when checking if a session is active
  reporter?.ignoreLogMessage('Received network error or non-101 status code.');
  reporter?.ignoreLogMessage('Connection was closed before it was established.');

  // Create the client.
  const wsClient = createWSClient({
    url: wsUrl,
    onError: (evt) => {
      const error = evt as { message?: string } | undefined;
      // On node evt is an Event with a message property
      // On browser it is an Event with no relevant information
      // We can't directly log or stringify the event as it will be undefined
      if (error?.message) {
        console.error(`[Cloudpack] Websocket client error: ${error.message}`);
      } else {
        console.error('[Cloudpack] Unexpected websocket client error');
      }
    },
    onOpen() {
      onOpen?.();
    },
  });
  const tempUrl = makeUrl(wsUrl);

  // Replace the protocol with http so that we can use the same url for http requests.
  const isSSL = tempUrl.protocol === 'wss:';
  tempUrl.protocol = isSSL ? 'https:' : 'http:';

  const httpUrl = tempUrl.toString().replace(/\/$/, '');
  const client = createTRPCClient<AppRouter>({
    links: [
      // send subscriptions to the websocket client and handle operations with HTTP requests
      splitLink({
        condition(op) {
          return op.type === 'subscription';
        },
        true: wsLink({
          client: wsClient,
        }),
        false: httpBatchLink({
          url: httpUrl,
        }),
      }),
    ],
  });

  const close: CloudpackClient['close'] = async () => {
    await wsClient.close();
  };

  // Add a default onError handler for onDataChanged subscriptions.
  // Otherwise errors (such as from zod type violations) will be hidden.
  const onDataChanged: CloudpackClient['onDataChanged'] = {
    subscribe: (source, opts) => {
      opts = { ...opts };
      opts.onError ??= (err) => {
        console.error(
          `[Cloudpack] Error in ${source.path.join('.')} data subscription (this may be a Cloudpack bug): ` +
            err.message,
        );
      };
      return client.onDataChanged.subscribe(source, opts);
    },
  };

  // Ugh. This is a hack to get around the fact that the client is a proxy.
  // Make another proxy which defines the close method and onDataChanged wrapper,
  // and passes through everything else to the original client.
  return new Proxy<CloudpackClient>({ close, onDataChanged } as CloudpackClient, {
    get(target, prop) {
      // eslint-disable-next-line no-prototype-builtins
      if (target.hasOwnProperty(prop)) {
        return target[prop as keyof typeof target];
      }

      return client[prop as Exclude<keyof CloudpackClient, 'close'>];
    },
  });
}
